'use client';

import Rollbar from 'rollbar';

/**
 * Client-side Rollbar for the v2 App Router error boundaries (error.tsx,
 * global-error.tsx). The legacy pages/ app boots Rollbar from the inline
 * snippet in components/global/HeadScripts.tsx (setting window.Rollbar), but
 * app/layout.tsx deliberately omits that legacy chrome — so app/ routes have no
 * browser Rollbar until this module lazily creates one.
 *
 * Mirrors the snippet's config knobs (token, environment gate, source-map
 * symbolication against NEXT_BUILD_ID) so client items line up with the server
 * ones from server/rollbar.ts. Items are tagged `framework: 'app-router'` to
 * match the server side.
 */
const CLIENT_TOKEN = process.env.NEXT_PUBLIC_ROLLBAR_CLIENT_TOKEN ?? '';

// Client can't read RAILWAY_ENVIRONMENT_NAME (not a NEXT_PUBLIC var), so mirror
// HeadScripts.tsx: the build-baked NEXT_PUBLIC_ROLLBAR_ENV, else NODE_ENV.
const ENV =
  process.env.NEXT_PUBLIC_ROLLBAR_ENV || process.env.NODE_ENV || 'development';

let instance: Rollbar | null = null;

// Constructed on first browser use only: error.tsx / global-error.tsx are
// 'use client' but still SSR, and the browser Rollbar build touches `window`.
function getClientRollbar(): Rollbar | null {
  if (typeof window === 'undefined') return null;

  instance ??= new Rollbar({
    accessToken: CLIENT_TOKEN,
    captureUncaught: false,
    captureUnhandledRejections: false,
    enabled: ENV !== 'development' && Boolean(CLIENT_TOKEN),
    environment: ENV,
    payload: {
      client: {
        javascript: {
          code_version: process.env.NEXT_BUILD_ID,
          guess_uncaught_frames: true,
          source_map_enabled: true,
        },
      },
      framework: 'app-router',
    },
  });

  return instance;
}

/** Report an error caught by an App Router client error boundary. */
export function reportClientError(
  error: unknown,
  context: Record<string, unknown> = {}
): void {
  const err = error instanceof Error ? error : new Error(String(error));

  getClientRollbar()?.error(err, context);
}
